<template>
  <div class="p-[20px] md:p-[50px] lg:p-[100px] bg-gray-50">
    <div class="flex flex-col items-center mb-12">
      <h1 class="text-2xl md:text-4xl font-semibold text-gray-900 mb-4 mt-10">
        {{ getLocalized1("title") }}
      </h1>
      <div class="w-16 md:w-20 h-1 bg-sky-500 rounded-full"></div>
    </div>

    <!-- Contact Info Section -->
    <div class="container mx-auto">
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-12">
        <!-- Phone Contact Card -->
        <div
          class="bg-white rounded-xl p-6 shadow-sm w-full hover:shadow-md transition-shadow duration-300 ease-in-out"
        >
          <div class="flex items-start gap-2">
            <div class="bg-sky-500 rounded-full p-3 md:p-4 shrink-0">
              <!-- Phone Icon -->
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-5 h-5 md:w-6 md:h-6 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                />
              </svg>
            </div>
            <div class="flex flex-col">
              <span class="text-gray-600 font-medium text-xs md:text-sm mb-1">{{
                getLocalized1("phone")
              }}</span>
              <a
                :href="'tel:' + companyInfo.primary_phone"
                class="text-gray-900 text-base md:text-lg font-semibold hover:text-sky-500 transition-colors"
              >
                {{ companyInfo.primary_phone }}
              </a>
              <p class="text-gray-500 text-xs md:text-sm mt-1 md:mt-2">
                {{ getLocalized1("phoneq") }}
              </p>
            </div>
          </div>
        </div>

        <!-- Email Contact Card -->
        <div
          class="bg-white rounded-xl p-6 shadow-sm w-full hover:shadow-md transition-shadow duration-300 ease-in-out"
        >
          <div class="flex items-start gap-4">
            <div class="bg-sky-500 rounded-full p-3 md:p-4 shrink-0">
              <!-- Email Icon -->
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-5 h-5 md:w-6 md:h-6 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <div class="flex flex-col">
              <span class="text-gray-600 font-medium text-xs md:text-sm mb-1">{{
                getLocalized1("email")
              }}</span>
              <a
                :href="'mailto:' + companyInfo.email"
                class="text-gray-900 text-base md:text-lg font-semibold hover:text-sky-500 transition-colors"
              >
                {{ companyInfo.email }}
              </a>
              <p class="text-gray-500 text-xs md:text-sm mt-1 md:mt-2">
                {{ getLocalized1("question") }}
              </p>
            </div>
          </div>
        </div>

        <!-- Telegram Card -->
        <div
          class="bg-white rounded-xl p-6 shadow-sm w-full hover:shadow-md transition-shadow duration-300 ease-in-out"
        >
          <div class="flex items-start gap-4">
            <div class="bg-sky-500 rounded-full p-3 md:p-4 shrink-0">
              <!-- Telegram Icon -->
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-5 h-5 md:w-6 md:h-6 text-white"
                viewBox="0 0 24 24"
              >
                <path
                  fill="none"
                  stroke="white"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="m11.985 15.408l3.242 3.686c1.2 1.365 1.801 2.048 2.43 1.881c.628-.166.844-1.064 1.275-2.861l2.39-9.968c.665-2.768.997-4.151.259-4.834s-2.017-.175-4.575.84L5.14 8.865c-2.046.813-3.069 1.219-3.134 1.917a1 1 0 0 0 0 .214c.063.699 1.084 1.108 3.128 1.927c.925.371 1.388.557 1.72.912q.056.06.108.124c.306.38.436.88.697 1.876l.489 1.867c.253.97.38 1.456.713 1.522s.622-.336 1.201-1.141zm0 0l-.317-.33c-.362-.378-.543-.566-.543-.8s.18-.423.543-.8l3.573-3.724"
                />
              </svg>
            </div>
            <div class="flex flex-col">
              <span class="text-gray-600 font-medium text-xs md:text-sm mb-1">{{
                getLocalized1("telegram")
              }}</span>
              <span class="text-gray-900 text-base md:text-lg font-semibold">
                <a
                  :href="companyInfo.telegram_link"
                  class="hover:text-sky-500 transition-colors"
                >
                  {{ getLocalized1("joinchannel") }}
                </a>
              </span>
              <p class="text-gray-500 text-xs md:text-sm mt-1 md:mt-2">
                {{ getLocalized1("jointelegram") }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Address & Google Map Section -->
    <div class="flex flex-col md:flex-row gap-8">
      <!-- Address Section -->
      <div class="flex-1">
        <div class="flex flex-col gap-4">
          <h1 class="text-lg font-semibold">{{ getLocalized1("address") }}</h1>
          <p class="text-base text-gray-600 md:w-[374px] md:h-[72px]">
            {{ getLocalized(companyInfo, "address") }}
          </p>
        </div>
        <!-- QR Code Section -->
        <div class="mt-8 flex flex-col">
          <h1 class="text-lg font-semibold">Find us by scanning the QR code</h1>
          <img class="w-[128px] h-[128px] mt-4" :src="qrmap" alt="QR Code" />
        </div>
      </div>

      <!-- Google Map Section -->
      <div
        class="flex-1 w-full md:w-[740px] h-[300px] md:h-[340px] rounded-[20px]"
      >
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3908.0311795325106!2d104.8894780751691!3d11.621127588583711!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x310953b2b56f79e7%3A0xb358854e6c07edb!2sTURBO%20TECH!5e0!3m2!1sen!2skh!4v1732019317468!5m2!1sen!2skh"
          width="100%"
          height="100%"
          allowfullscreen=""
          loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { useLocalization } from "@/composable/useLocalization";
import qrmap from "@/assets/pictures/QR_map.png";
export default {
  data() {
    return {
      companyInfo: {},
      api: process.env.VUE_APP_API_KEY,
      qrmap: qrmap,
      translations: {
        title: {
          en: "Get in Touch With Us ",
          kh: "ទំនាក់ទំនងមកយើង",
        },
        question: {
          en: "Send us your questions anytime",
          kh: "ផ្ញើសំណួររបស់អ្នកមកយើងគ្រប់ពេល",
        },
        email: {
          en: "Email",
          kh: "អ៊ីមែល",
        },
        telegram: {
          en: "Telegram",
          kh: "តេឡេក្រាម",
        },
        address: {
          en: "Address",
          kh: "អាស័យដ្ឋាន",
        },
        joinchannel: {
          en: "Join Our Channel",
          kh: "ចូល Channel របស់យើង",
        },
        jointelegram: {
          en: "Join Our Telegram Channel",
          kh: "ចូល Channel តេលេក្រាមរបស់យើង",
        },
        phone: {
          en: "Phone",
          kh: "ទូរស័ព្ទ",
        },
        phoneq: {
          en: "Available 24/7 for your queries",
          kh: "មានចម្ងល់អាចទំនាក់ទំនងមកយើងបាន ២៤ម៉ោង",
        },
      },
      currentLang: localStorage.getItem("language") || "en",
    };
  },
  computed: {
    // Optionally, you can use a computed property for localization logic
    getLocalized() {
      const { getLocalized } = useLocalization();
      return getLocalized;
    },
    getLocalized1() {
      return (key) => {
        return this.translations[key]
          ? this.translations[key][this.currentLang]
          : key;
      };
    },
  },
  mounted() {
    this.fetchCompanyInfo();
  },
  methods: {
    async fetchCompanyInfo() {
      try {
        const response = await axios.get(this.api + "/api/company-info");
        this.companyInfo = response.data.items.find((item) => item.id === 1);
      } catch (e) {
        console.error(e.message);
      }
    },
  },
};
</script>
